export type ProbeResult = 'reachable' | 'unreachable' | 'offline' | 'pending' | 'unknown';

const CACHE_TTL_MS = 30_000;
const PROBE_TIMEOUT_MS = 3_000;

interface CacheEntry {
  result: ProbeResult;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<ProbeResult>>();

export function getCachedProbeResult(origin: string): ProbeResult {
  const entry = cache.get(origin);
  if (!entry || entry.expiresAt < Date.now()) return 'unknown';
  return entry.result;
}

export function getProbeResult(origin: string): Promise<ProbeResult> {
  const cached = cache.get(origin);
  if (cached && cached.expiresAt >= Date.now()) {
    return Promise.resolve(cached.result);
  }
  const existing = inFlight.get(origin);
  if (existing) return existing;

  const promise = runProbe(origin).then((result) => {
    cache.set(origin, { result, expiresAt: Date.now() + CACHE_TTL_MS });
    inFlight.delete(origin);
    return result;
  });
  inFlight.set(origin, promise);
  return promise;
}

async function runProbe(origin: string): Promise<ProbeResult> {
  if (!navigator.onLine) return 'offline';
  try {
    await fetch(origin, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return 'reachable';
  } catch {
    return navigator.onLine ? 'unreachable' : 'offline';
  }
}

export function _resetProbeCacheForTests(): void {
  cache.clear();
  inFlight.clear();
}
